import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { openLmsPrintPdfPreview } from '@/components/admin/print/LmsPrintPreviewPdfButton'
import { memberApi } from '@/api/b2b'
import { learnerStatusApi } from '@/api/learnerStatus'
import { useCurrentEntitlement } from '@/hooks/admin/useContentFee'
import { useLearnerToday } from '@/hooks/coaching/useLearnerToday'
import { useTestCompletionCapability } from '@/hooks/coaching/useTestCompletion'
import { useTests } from '@/hooks/coaching/useTests'
import type {
  ClassRosterItemView,
  LessonSelectionMeta,
  LearnerHeaderView,
  LearnerSummaryStatsView,
  LcTestKindKey,
  RosterAttendanceStatus,
  TestSelection,
} from '@/types/coaching'
import { ResetPasswordModal } from '@/components/admin/members/ResetPasswordModal'
import { ResetLearningProgressModal } from '@/components/admin/members/ResetLearningProgressModal'
import { CompletedTestListModal } from '@/components/admin/members/CompletedTestListModal'
import { ContentTreeSection } from '@/components/admin/coaching/ContentTreeSection'
import { LessonGradingPane } from '@/components/admin/coaching/LessonGradingPane'
import { LessonProgressDetailPane } from '@/components/admin/coaching/LessonProgressDetailPane'
import { ContentFeeEntitlementPanel } from '@/components/admin/contentFee/ContentFeeEntitlementPanel'
import { ContentFeeOrderHistoryPanel } from '@/components/admin/contentFee/ContentFeeOrderHistoryPanel'
import { ContentFeePurchaseModal } from '@/components/admin/contentFee/ContentFeePurchaseModal'
import { TextbookRequestPanel } from '@/components/admin/contentFee/TextbookRequestPanel'
import { LearnerConsultationPanel } from '@/components/admin/members/LearnerConsultationPanel'
import { LearnerHistoryHeader } from '@/components/admin/learner-history/LearnerHistoryHeader'
import { StudentDetailTabs, type StudentDetailTab } from '@/pages/admin/studentDetail/StudentDetailTabs'
import { StudentSessionHistory } from '@/pages/admin/studentDetail/StudentSessionHistory'

const ATTENDANCE_STATUSES = new Set<RosterAttendanceStatus>(['IN', 'OUT', 'ABSENT', 'LEAVE', 'NONE'])
const DETAIL_TAB_IDS = ['learning', 'sessions', 'enrollment', 'textbook', 'consultation'] as const

function normalizeAttendanceStatus(status: string | null | undefined): RosterAttendanceStatus {
  return status && ATTENDANCE_STATUSES.has(status as RosterAttendanceStatus)
    ? status as RosterAttendanceStatus
    : 'NONE'
}

export default function StudentDetail() {
  const { t } = useTranslation(['admin', 'coaching', 'common'])
  const { userId } = useParams<{ userId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const numericId = userId ? Number(userId) : NaN
  const validId = Number.isInteger(numericId) && numericId > 0
  const tabParam = searchParams.get('tab')
  const activeTabId = DETAIL_TAB_IDS.includes(tabParam as typeof DETAIL_TAB_IDS[number])
    ? tabParam as typeof DETAIL_TAB_IDS[number]
    : 'learning'

  const queryClient = useQueryClient()
  const [resetPwTarget, setResetPwTarget] = useState<{ userId: number; fullName: string } | null>(null)
  const [resetProgTarget, setResetProgTarget] = useState<{ userId: number; fullName: string } | null>(null)
  const [contentFeePurchaseOpen, setContentFeePurchaseOpen] = useState(false)
  const [testListKind, setTestListKind] = useState<LcTestKindKey | null>(null)
  const [selectedLesson, setSelectedLesson] = useState<{ id: number; meta: LessonSelectionMeta } | null>(null)

  const memberQ = useQuery({
    queryKey: ['member', numericId],
    queryFn: async () => (await memberApi.getById(numericId)).data.data,
    enabled: validId,
  })
  const summaryQ = useQuery({
    queryKey: ['learnerSummary', numericId],
    queryFn: async () => (await learnerStatusApi.getSummary(numericId)).data.data,
    enabled: validId,
  })
  const testCompletionCapabilityQ = useTestCompletionCapability()
  const testsQ = useTests(numericId)
  const entitlementQ = useCurrentEntitlement(numericId)
  const learnerTodayQ = useLearnerToday(numericId, undefined, validId && activeTabId === 'learning')
  const handleSelectLesson = (lessonId: number, meta: LessonSelectionMeta) => {
    setSelectedLesson({ id: lessonId, meta })
  }

  const handlePrintTest = (sel: TestSelection, downloadPdf = false) => {
    const match = testsQ.data?.tests.find(
      (tt) => tt.kind === sel.kind && tt.chapterOrdinal === sel.chapterOrdinal && tt.scopeContentId === sel.scopeContentId,
    )
    if (!match) {
      alert(t('admin:students.detail.printTestUnavailable'))
      return
    }
    const href = `/admin/coaching/print/${numericId}/${sel.scopeContentId}/${sel.kind.toLowerCase()}?testId=${match.testId}`
    if (downloadPdf) {
      openLmsPrintPdfPreview(href)
      return
    }
    window.open(
      href,
      '_blank',
      'noopener,noreferrer',
    )
  }

  if (!validId || memberQ.isError) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center text-gray-600">
        {t('admin:students.detail.notFound')}
      </div>
    )
  }
  if (memberQ.isLoading || !memberQ.data) {
    return <p className="py-16 text-center text-sm text-gray-400">{t('admin:students.detail.loading')}</p>
  }

  const member = memberQ.data
  const isTestCenter = testCompletionCapabilityQ.data?.operationMode === 'TEST'
  const handleTabChange = (tabId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (tabId === 'learning') {
        next.delete('tab')
      } else {
        next.set('tab', tabId)
      }
      return next
    }, { replace: true })
  }
  const summary = summaryQ.data
  const learnerStatusLabel = summary
    ? t(`admin:students.memberStatus.${summary.status}`, { defaultValue: summary.status })
    : undefined
  const learnerHeader: LearnerHeaderView = {
    learnerId: numericId,
    fullName: member.fullName,
    className: member.classNames?.join(', ') || undefined,
    productTitle: member.linkedProductName ?? '-',
    sessionRound: null,
    lessonQuota: entitlementQ.data
      ? {
          usedLessons: entitlementQ.data.cumulativeUsedLessons,
          grantedLessons: entitlementQ.data.cumulativeGrantedLessons,
          remainingLessons: entitlementQ.data.remaining,
        }
      : null,
  }
  const learnerStats: LearnerSummaryStatsView = {
    elapsedSeconds: 0,
    completedLessonCount: 0,
    inProgressLessonCount: 0,
    avgCorrectRate: 0,
  }
  const rosterItem: ClassRosterItemView = {
    userId: member.userId,
    fullName: member.fullName,
    email: member.email,
    gradeLevel: member.gradeLevel ?? null,
    gender: member.gender ?? null,
    guardianPhone: member.guardian?.phone ?? null,
    attendanceStatus: normalizeAttendanceStatus(summary?.today.attendanceStatus),
  }
  const selectedCurrentLesson = selectedLesson?.meta.status === 'in_progress'
    ? learnerTodayQ.data?.lessons.find((lesson) => lesson.lessonId === selectedLesson.id) ?? null
    : null

  const tabs: StudentDetailTab[] = [
    {
      id: 'learning',
      label: t('admin:students.detail.tabs.learning'),
      render: () => (
        <div className="grid grid-cols-1 gap-0 rounded-lg border border-gray-200 bg-white lg:grid-cols-[300px_1fr]">
          <div className="border-b border-gray-100 lg:border-b-0 lg:border-r">
            <ContentTreeSection
              learnerId={numericId}
              onSelectLesson={handleSelectLesson}
              selectedLessonId={selectedLesson?.id}
              onSelectTest={(selection) => handlePrintTest(selection)}
              onDownloadTest={(selection) => handlePrintTest(selection, true)}
            />
          </div>
          <div className="p-3">
            {selectedLesson?.meta.status === 'completed' ? (
              <LessonProgressDetailPane
                learnerId={numericId}
                lessonId={selectedLesson.id}
                selectionMeta={selectedLesson.meta}
              />
            ) : selectedLesson?.meta.status === 'in_progress' && selectedCurrentLesson ? (
              <LessonGradingPane
                learnerId={numericId}
                lesson={selectedCurrentLesson}
                selectionMeta={selectedLesson.meta}
              />
            ) : selectedLesson?.meta.status === 'in_progress' && learnerTodayQ.isLoading ? (
              <div className="rounded-lg border border-gray-200 bg-white p-8 text-sm text-gray-500" role="status" aria-live="polite">
                {t('coaching:lessonDetail.loading')}
              </div>
            ) : selectedLesson?.meta.status === 'in_progress' ? (
              <div className="rounded-lg border border-gray-200 bg-white p-8 text-sm text-red-600" role="alert">
                {t('coaching:lessonDetail.loadError')}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-12 text-center text-sm text-gray-500" role="status">
                {t('coaching:library.selectLessonPrompt')}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      id: 'sessions',
      label: t('admin:students.detail.tabs.sessions'),
      render: () => <StudentSessionHistory userId={numericId} />,
    },
    {
      id: 'enrollment',
      label: t('admin:students.detail.tabs.enrollment'),
      render: () => (
        <div className="space-y-4 py-4">
          <ContentFeeEntitlementPanel userId={numericId} />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setContentFeePurchaseOpen(true)}
              className="inline-flex items-center rounded-lg bg-primary-600 px-3 py-2 text-sm text-white hover:bg-primary-700"
            >
              {t('admin:students.detail.contentFee.purchaseButton')}
            </button>
          </div>
          <ContentFeeOrderHistoryPanel userId={numericId} />
        </div>
      ),
    },
    {
      id: 'textbook',
      label: t('admin:students.detail.tabs.textbook'),
      render: () => (
        <div className="space-y-4 py-4">
          <TextbookRequestPanel userId={numericId} />
        </div>
      ),
    },
    {
      id: 'consultation',
      label: t('admin:students.detail.tabs.consultation'),
      render: () => (
        <div className="py-4">
          <LearnerConsultationPanel userId={numericId} />
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="sticky top-12 z-10 shadow-sm">
        <LearnerHistoryHeader
          learner={learnerHeader}
          summary={learnerStats}
          rosterItem={rosterItem}
          onTestAverageClick={setTestListKind}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setResetPwTarget({ userId: member.userId, fullName: member.fullName })}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                {t('admin:students.rowMenu.resetPassword')}
              </button>
              {isTestCenter ? (
                <button
                  type="button"
                  onClick={() => setResetProgTarget({ userId: member.userId, fullName: member.fullName })}
                  className="rounded-lg border border-red-200 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                >
                  {t('admin:students.rowMenu.resetProgress')}
                </button>
              ) : null}
            </div>
          }
        />
      </div>

      <StudentDetailTabs
        tabs={tabs}
        ariaLabel={t('admin:students.detail.tabsLabel')}
        activeId={activeTabId}
        onActiveIdChange={handleTabChange}
      />
      <ResetPasswordModal target={resetPwTarget} onClose={() => setResetPwTarget(null)} />
      {isTestCenter ? (
        <ResetLearningProgressModal
          target={resetProgTarget}
          onClose={() => setResetProgTarget(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['learnerSummary', numericId] })
            queryClient.invalidateQueries({ queryKey: ['coaching', 'learner', numericId] })
          }}
        />
      ) : null}
      <ContentFeePurchaseModal
        userId={numericId}
        learnerStatusLabel={learnerStatusLabel}
        open={contentFeePurchaseOpen}
        onClose={() => setContentFeePurchaseOpen(false)}
      />
      {testListKind && (
        <CompletedTestListModal
          learnerId={numericId}
          kind={testListKind}
          learnerName={member.fullName}
          onClose={() => setTestListKind(null)}
        />
      )}
    </div>
  )
}
